import React, { useState, useEffect } from 'react';
import './StaggeredTestimonials.scss';

// Real, verifiable Google reviews for Pulpit Cabs (4.7 stars, 41 reviews).
// Captured verbatim from the public Google Business listing, no invented
// names, quotes, or counts. Link to the live listing below.
const GOOGLE_REVIEWS_URL = "https://www.google.com/maps?cid=14744859779437648009";
const GOOGLE_RATING = "4.7";
const GOOGLE_REVIEW_COUNT = 41;

const testimonials = [
  {
    quote: "Used Pulpit for a weekend getaway. The driver was polite, calm, and well-mannered. He followed Google Maps but also suggested a better route to avoid traffic. Overall, a pleasant journey.",
    author: "Sudhanshu Varun",
    role: "Weekend getaway"
  },
  {
    quote: "The booking experience is extremely smooth. Took less than a minute to confirm my ride. Driver details were shared clearly and the cab arrived exactly on time. Very professional service.",
    author: "Mayank Sharma",
    role: "Booking experience"
  },
  {
    quote: "Had an early morning railway station drop. Booking was super easy and confirmation was instant. Cab arrived on time and the driver was polite and professional. Great service.",
    author: "Kashish Khubchandani",
    role: "Railway station drop"
  },
  {
    quote: "Took a late-night ride and felt completely safe throughout the journey. The driver maintained professionalism and drove responsibly. Big thumbs up.",
    author: "Swati Jadav",
    role: "Late-night ride"
  },
  {
    quote: "I was visiting Pune for the first time and used Pulpit for local sightseeing. The driver was very friendly and guided us about good places to visit and eat. Made the trip very convenient and stress-free.",
    author: "Densi Thummar",
    role: "Pune sightseeing"
  },
  {
    quote: "One of the best corporate cab service providers. They handle employee transportation very efficiently. Always on time and very professional drivers.",
    author: "Harshit Prajapati",
    role: "Corporate travel"
  },
  {
    quote: "Excellent cab service in Gandhinagar. The taxi was clean and the driver was very professional. Best option for airport transfer and outstation travel. Highly recommended!",
    author: "Anurag Tiwari",
    role: "Airport & outstation"
  },
  {
    quote: "Had a really great experience with Pulpit for a group family tour to Dwarka and Somnath. From booking to the actual trip, everything was smooth and well-organized.",
    author: "Umesh Prajapati",
    role: "Family tour to Dwarka & Somnath"
  },
  {
    quote: "The booking experience on the website was simple and quick. Took less than a minute to confirm. Driver details were shared clearly. Overall very convenient.",
    author: "Vaidehi Patel",
    role: "Website booking"
  }
];

// Deterministic brand colour per author for the initials avatar.
const AVATAR_COLORS = ["#21A5A0", "#E05A49", "#0B1F3A", "#2DB8B3"];
const initialsOf = (name) =>
  name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// The multi-colour Google mark, so the reviews read unmistakably as Google reviews.
const GoogleG = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ display: "block", flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

// Five stars with an accurate fractional fill for the aggregate rating.
const Stars = ({ rating = 5, size = 16 }) => {
  const pct = Math.max(0, Math.min(100, (rating / 5) * 100));
  return (
    <span className="testimonial-stars" aria-hidden="true" style={{ position: "relative", display: "inline-block", fontSize: `${size}px`, lineHeight: 1, letterSpacing: "1px" }}>
      <span style={{ color: "#d8dedc" }}>★★★★★</span>
      <span style={{ position: "absolute", top: 0, left: 0, width: `${pct}%`, overflow: "hidden", color: "#f5a623", whiteSpace: "nowrap" }}>★★★★★</span>
    </span>
  );
};

const TestimonialSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <section className="testimonial-section" aria-label="Customer testimonials">
      <div className="testimonial-section__container">
        <header className="testimonial-section__header">
          <span className="testimonial-section__subtitle">Rider Stories</span>
          <h2 className="testimonial-section__title">What Our Riders Say</h2>
          <p className="testimonial-section__description">
            Real reviews from riders across India who trust Pulpit Cabs for safe, reliable, and comfortable journeys.
          </p>

          {/* Real, verifiable Google rating, links straight to the live listing. */}
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noreferrer"
            className="testimonial-section__rating"
            style={{
              display: "inline-flex", alignItems: "center", gap: "12px", marginTop: "22px",
              padding: "10px 18px 10px 14px", borderRadius: "999px", background: "#ffffff",
              border: "1px solid #e6ecea", boxShadow: "0 6px 20px rgba(16,40,37,0.06)",
              textDecoration: "none"
            }}
          >
            <GoogleG size={24} />
            <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.1 }}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "17px", fontWeight: 800, color: "#1a1a1a" }}>{GOOGLE_RATING}</span>
                <Stars rating={Number(GOOGLE_RATING)} size={15} />
              </span>
              <span style={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", marginTop: "3px" }}>
                {GOOGLE_REVIEW_COUNT} Google reviews
              </span>
            </span>
            <span style={{ fontSize: "12.5px", fontWeight: 800, color: "#21A5A0", whiteSpace: "nowrap" }}>Read on Google →</span>
          </a>
        </header>

        <div className="testimonial-section__content">
          <blockquote className="testimonial-section__quote">
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
              <GoogleG size={16} />
              <span style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6b7280" }}>
                Verified Google review
              </span>
            </div>
            <p className="testimonial-section__text">
              {currentTestimonial.quote}
            </p>
            <footer className="testimonial-section__footer">
              <div className="testimonial-section__author-info">
                <span
                  className="testimonial-section__avatar testimonial-section__avatar--initials"
                  style={{ backgroundColor: AVATAR_COLORS[currentIndex % AVATAR_COLORS.length] }}
                  aria-hidden="true"
                >
                  {initialsOf(currentTestimonial.author)}
                </span>
                <div>
                  <cite className="testimonial-section__author">
                    {currentTestimonial.author}
                  </cite>
                  <div className="testimonial-section__role">
                    {currentTestimonial.role}
                  </div>
                </div>
              </div>
            </footer>
          </blockquote>

          <nav className="testimonial-section__navigation" aria-label="Testimonials navigation">
            <button
              onClick={prevTestimonial}
              className="testimonial-section__btn testimonial-section__btn--prev"
              aria-label="Previous testimonial"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            <button
              onClick={nextTestimonial}
              className="testimonial-section__btn testimonial-section__btn--next"
              aria-label="Next testimonial"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          </nav>

          <div className="testimonial-section__indicators">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`testimonial-section__indicator ${index === currentIndex ? 'testimonial-section__indicator--active' : ''}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
